import React, { useEffect, useState } from 'react';
import { Button, Box, Alert, CircularProgress } from '@mui/material';
import { ArrowBack, Check, Link as LinkIcon } from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import config from '../../config/env.config';
import { userService } from '../../services/user.service';
import { UserProfile } from '../../types/user.types';
import { STRAVA_ORANGE } from '../../theme/appColorTokens';
import { StatusPill } from '../ui/StatusPill';
import { cardSx } from '../ui/uiHelpers';

const CONNECT_POINT_KEYS = ['autoSync', 'autoResults', 'disconnectAnytime'] as const;

// Error codes the backend appends to the Strava callback redirect.
const KNOWN_ERROR_CODES = [
    'already_connected',
    'strava_already_connected',
    'user_not_found',
    'token_exchange_failed',
    'missing_code',
    'invalid_token',
    'server_error',
    'unknown_error',
];

const API_URL = config.API_URL;

export const StravaConnect = () => {
    const { t } = useTranslation('auth');
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [profileLoading, setProfileLoading] = useState(true);

    const isConnectedToStrava = userProfile?.stravaProfile != null;

    // Fetch user profile to check connection status
    useEffect(() => {
        let isMounted = true;

        const fetchUserProfile = async () => {
            try {
                const response = await userService.getCurrentUserProfile();
                if (isMounted) {
                    setUserProfile(response.data);
                }
            } catch (error) {
                console.error('Failed to fetch user profile:', error);
            } finally {
                if (isMounted) {
                    setProfileLoading(false);
                }
            }
        };

        fetchUserProfile();

        return () => {
            isMounted = false;
        };
    }, []);

    // Handle redirect parameters
    useEffect(() => {
        const success = searchParams.get('success');
        const error = searchParams.get('error');

        if (success === 'true') {
            // Show success message briefly before redirect
            setTimeout(() => {
                navigate('/', { state: { stravaConnected: true } });
            }, 1500);
        } else if (error) {
            console.error('Strava connection error:', error);
            // Don't auto-redirect on error, let user see the error message
        }
    }, [searchParams, navigate]);

    const handleConnect = async () => {
        try {
            // No need to check localStorage - tokens are in httpOnly cookies
            // Redirect to backend's Strava connect endpoint
            window.location.href = `${API_URL}/connect/strava`;
        } catch (error) {
            console.error('Error connecting to Strava:', error);
        }
    };

    const getErrorMessage = (error: string) =>
        KNOWN_ERROR_CODES.includes(error)
            ? t(`strava.errors.${error}`)
            : t('strava.errors.default');

    // If user is already connected and no success/error params, redirect to dashboard
    useEffect(() => {
        if (!profileLoading && isConnectedToStrava && !searchParams.get('success') && !searchParams.get('error')) {
            navigate('/', { replace: true });
        }
    }, [profileLoading, isConnectedToStrava, searchParams, navigate]);

    if (profileLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
            <Box sx={{ width: '100%', maxWidth: 440 }}>
                <Button startIcon={<ArrowBack />} onClick={() => navigate('/')} variant="outlined">
                    {t('strava.backToDashboard')}
                </Button>
            </Box>

            <Box sx={[cardSx, { width: '100%', maxWidth: 440, p: { xs: 2.5, sm: 4 }, display: 'flex', flexDirection: 'column', gap: 3 }]}>
                {searchParams.get('success') && (
                    <Alert severity="success">{t('strava.connectedRedirecting')}</Alert>
                )}
                {searchParams.get('error') && (
                    <Alert severity="error">
                        {getErrorMessage(searchParams.get('error') || 'unknown_error')}
                    </Alert>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                        <Box component="h1" sx={{ m: 0, fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em' }}>
                            {isConnectedToStrava ? t('strava.titleConnected') : t('strava.title')}
                        </Box>
                        {isConnectedToStrava && <StatusPill tone="success" label={t('strava.connected')} dot />}
                    </Box>
                    <Box sx={{ fontSize: 14, lineHeight: 1.5, color: 'var(--muted)' }}>
                        {isConnectedToStrava
                            ? t('strava.descriptionConnected')
                            : t('strava.description')}
                    </Box>
                </Box>

                {!isConnectedToStrava && (
                    <>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {CONNECT_POINT_KEYS.map(point => (
                                <Box key={point} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', fontSize: 14, lineHeight: 1.45 }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            flex: 'none', width: 22, height: 22, borderRadius: '999px',
                                            backgroundColor: 'var(--accent-bg)', color: 'var(--accent-fg)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}
                                    >
                                        <Check sx={{ fontSize: 14 }} />
                                    </Box>
                                    {t(`strava.points.${point}`)}
                                </Box>
                            ))}
                        </Box>
                        <Button
                            variant="contained"
                            onClick={handleConnect}
                            startIcon={<LinkIcon />}
                            sx={{
                                height: 42,
                                fontWeight: 600,
                                background: STRAVA_ORANGE,
                                color: '#fff',
                                '&:hover': { background: '#e34402' },
                            }}
                        >
                            {t('strava.connect')}
                        </Button>
                    </>
                )}
            </Box>
        </Box>
    );
};
